import type { ReactElement } from 'react';

/**
 * A row of tabs over one panel (People, Company; the menu consolidation, decisions 844 and 855), as a project's tabs:
 * the tab list, then `TabPanel` with the chosen tab's content. Only the tabs this person may see are passed.
 */
export function Tabs<T extends string>({
  label,
  tabs,
  current,
  onChange,
  idPrefix,
}: {
  readonly label: string;
  readonly tabs: ReadonlyArray<readonly [T, string]>;
  readonly current: T;
  readonly onChange: (tab: T) => void;
  /** Keeps the ids apart from another tab row on the page. */
  readonly idPrefix: string;
}): ReactElement {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map(([id, text]) => (
        <button
          key={id}
          type="button"
          role="tab"
          id={`${idPrefix}-tab-${id}`}
          aria-selected={current === id}
          aria-controls={`${idPrefix}-panel-${id}`}
          className={current === id ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
          onClick={() => onChange(id)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  current,
  children,
}: {
  readonly idPrefix: string;
  readonly current: string;
  readonly children: ReactElement | null;
}): ReactElement {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${current}`}
      aria-labelledby={`${idPrefix}-tab-${current}`}
      className="stack"
    >
      {children}
    </div>
  );
}
